/**
 * IFRAP-AI.fr - Comparaison Internationale
 * Tableau de bord comparatif France vs pays européens
 * Données Eurostat en temps réel
 */

import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api/client';
import { RefreshCw, TrendingDown, TrendingUp, Minus, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const COUNTRY_FLAGS = {
  FR: { flag: 'FR', name: 'France', color: '#000091' },
  DE: { flag: 'DE', name: 'Allemagne', color: '#DD0000' },
  IT: { flag: 'IT', name: 'Italie', color: '#008C45' },
  ES: { flag: 'ES', name: 'Espagne', color: '#AA151B' },
  BE: { flag: 'BE', name: 'Belgique', color: '#FDDA24' },
  CH: { flag: 'CH', name: 'Suisse', color: '#FF0000' },
};

const INDICATORS = [
  { key: 'dette_pib', label: 'Dette publique', unit: '% PIB', higherIsWorse: true },
  { key: 'deficit_pib', label: 'Déficit public', unit: '% PIB', higherIsWorse: true },
  { key: 'pib_habitant', label: 'PIB par habitant', unit: '€', higherIsWorse: false },
  { key: 'croissance', label: 'Croissance PIB', unit: '%', higherIsWorse: false },
  { key: 'emploi', label: "Taux d'emploi 20-64", unit: '%', higherIsWorse: false },
  { key: 'charge_dette', label: 'Charge de la dette', unit: '% PIB', higherIsWorse: true },
];

const ComparisonPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState('DE');
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/api/kpis/comparison');
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getCellColor = (frVal, otherVal, higherIsWorse) => {
    if (frVal == null || otherVal == null) return '';
    const diff = frVal - otherVal;
    if (Math.abs(diff) < 0.5) return 'text-gray-600';
    if (higherIsWorse) {
      return diff > 0 ? 'text-red-600 font-semibold' : 'text-green-600 font-semibold';
    }
    return diff > 0 ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold';
  };

  const getDiffIcon = (frVal, otherVal, higherIsWorse) => {
    if (frVal == null || otherVal == null) return null;
    const diff = frVal - otherVal;
    if (Math.abs(diff) < 0.5) return <Minus className="w-3 h-3 text-gray-400" />;
    const isWorse = higherIsWorse ? diff > 0 : diff < 0;
    return isWorse
      ? <TrendingDown className="w-3 h-3 text-red-500" />
      : <TrendingUp className="w-3 h-3 text-green-500" />;
  };

  const formatValue = (val, unit) => {
    if (val == null) return '-';
    if (unit === '€') return `${Math.round(val).toLocaleString('fr-FR')}`;
    return `${val.toFixed(1).replace('.', ',')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-[#000091] animate-spin" />
      </div>
    );
  }

  const countries = data?.countries || {};
  const frData = countries['FR'] || {};
  const otherCountries = Object.keys(COUNTRY_FLAGS).filter(c => c !== 'FR');

  return (
    <div className="bg-[#F8F9FA] min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate('/')}
            className="p-2 rounded-lg hover:bg-gray-200 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-[#000091]" data-testid="comparison-title">
              Comparaison internationale
            </h1>
            <p className="text-sm text-gray-500">
              France vs {otherCountries.length} pays - Données Eurostat {data?.year || ''}
            </p>
          </div>
          <button
            onClick={fetchData}
            className="ml-auto p-2 rounded-lg hover:bg-gray-200 transition-colors"
            data-testid="refresh-comparison-btn"
          >
            <RefreshCw className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg mb-6 text-sm">
            {error}
          </div>
        )}

        {/* Full comparison table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto mb-8">
          <table className="w-full text-sm" data-testid="comparison-table">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left px-4 py-3 font-medium text-gray-600">Indicateur</th>
                <th className="text-center px-3 py-3 font-bold text-[#000091] bg-blue-50">
                  France
                </th>
                {otherCountries.map(c => (
                  <th
                    key={c}
                    className={`text-center px-3 py-3 font-medium cursor-pointer hover:bg-gray-100 transition-colors ${
                      selectedCountry === c ? 'bg-yellow-50 font-bold' : 'text-gray-600'
                    }`}
                    onClick={() => setSelectedCountry(c)}
                  >
                    {COUNTRY_FLAGS[c].name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {INDICATORS.map((ind, i) => {
                const frVal = frData[ind.key];
                return (
                  <tr key={ind.key} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-800">{ind.label}</div>
                      <div className="text-xs text-gray-400">{ind.unit}</div>
                    </td>
                    <td className="text-center px-3 py-3 font-bold text-[#000091] bg-blue-50/50">
                      {formatValue(frVal, ind.unit)}
                    </td>
                    {otherCountries.map(c => {
                      const val = countries[c]?.[ind.key];
                      return (
                        <td
                          key={c}
                          className={`text-center px-3 py-3 ${getCellColor(frVal, val, ind.higherIsWorse)} ${
                            selectedCountry === c ? 'bg-yellow-50/50' : ''
                          }`}
                        >
                          <div className="flex items-center justify-center gap-1">
                            {getDiffIcon(frVal, val, ind.higherIsWorse)}
                            {formatValue(val, ind.unit)}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Bilateral comparison cards */}
        <h2 className="text-lg font-semibold text-gray-800 mb-4">
          France vs {COUNTRY_FLAGS[selectedCountry]?.name}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {INDICATORS.map(ind => {
            const frVal = frData[ind.key];
            const otherVal = countries[selectedCountry]?.[ind.key];
            const diff = frVal != null && otherVal != null ? frVal - otherVal : null;
            const isWorse = diff != null && (ind.higherIsWorse ? diff > 0 : diff < 0);

            return (
              <div
                key={ind.key}
                className={`bg-white rounded-lg border p-4 ${
                  isWorse ? 'border-red-200' : 'border-green-200'
                }`}
                data-testid={`comparison-card-${ind.key}`}
              >
                <div className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-2">
                  {ind.label}
                </div>
                <div className="flex items-center justify-between mb-1">
                  <div className="text-center">
                    <div className="text-lg font-bold text-[#000091]">
                      {formatValue(frVal, ind.unit)}
                    </div>
                    <div className="text-xs text-gray-400">France</div>
                  </div>
                  <div className="text-xs text-gray-300">vs</div>
                  <div className="text-center">
                    <div className="text-lg font-bold" style={{ color: COUNTRY_FLAGS[selectedCountry]?.color }}>
                      {formatValue(otherVal, ind.unit)}
                    </div>
                    <div className="text-xs text-gray-400">{COUNTRY_FLAGS[selectedCountry]?.name}</div>
                  </div>
                </div>
                {diff != null && (
                  <div
                    className={`text-center text-xs font-medium mt-2 py-1 rounded ${
                      isWorse ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-600'
                    }`}
                  >
                    {diff > 0 ? '+' : ''}{diff.toFixed(ind.unit === '€' ? 0 : 1).replace('.', ',')} {ind.unit}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Source */}
        <div className="mt-6 text-xs text-gray-400 text-center">
          Source : Eurostat Open Data | Dernière mise à jour : {data?.fetched_at || '-'}
        </div>
      </div>
    </div>
  );
};

export default ComparisonPage;
